// src/app/cascade-of-care/cascade-of-care.component.ts
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { AnalysisStore } from '../../../../core/shared/analysis.store';

type YearKey = string;
type CmShape = Record<string, Record<YearKey, number>>;

function isBaselineTitle(title: string | null | undefined): boolean {
    const t = (title || '').trim().toLowerCase();
    return t === 'simulation 1' || t === 'basic simulation';
}

@Component({
    selector: 'app-dashboard-costmetrics',
    standalone: true,
    imports: [CommonModule, PopoverModule, ButtonModule],
    templateUrl: './dashboard-costmetrics.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardCostmetricsComponent implements OnInit, OnDestroy {
    private analysisStore = inject(AnalysisStore);
    private cdr = inject(ChangeDetectorRef);
    private sub = new Subscription();

    // NEW: selected simulation name observable
    readonly selectedSimulation$ = combineLatest([this.analysisStore.selectedRunId$, this.analysisStore.runsList$]).pipe(
        map(([id, runs]) => {
            const run = id ? runs.find(r => r.id === id) : (runs.length ? runs[runs.length - 1] : undefined);
            return run?.title ?? 'Simulation 1 (Baseline)';
        })
    );

    // Displays
    c4cInterventionCostDisplay = '—';
    healthcareSavingsDisplay = '—';
    netCostDisplay = '—';
    qalyDisplay = '—';
    netBenefitDisplay = '—';

    showNoSimInfo = false;

    private readonly targetYear: YearKey = '2040';

    ngOnInit(): void {
        this.sub.add(
            this.analysisStore.runsList$.subscribe(() => {
                this.recomputeFromRuns();
            })
        );
        this.recomputeFromRuns();
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    private recomputeFromRuns(): void {
        const runs = this.analysisStore.getRunsSnapshot();
        if (!runs || runs.length === 0) {
            this.resetDisplays(true);
            this.cdr.markForCheck();
            return;
        }

        const baseRun = runs.find(r => isBaselineTitle(r.title)) ?? runs[0];
        const others = runs.filter(r => !isBaselineTitle(r.title));
        this.showNoSimInfo = others.length === 0;
        const othersWithCost = others.filter(r => !!(r as any).costCm);

        const currentRun =
            (othersWithCost.length > 0 ? othersWithCost[othersWithCost.length - 1] : undefined) ??
            (others.length > 0 ? others[others.length - 1] : baseRun);

        const baseCm = this.pickCostCm(baseRun);
        const currentCm = this.pickCostCm(currentRun);

        if (!currentCm) {
            this.resetDisplays(false);
            this.cdr.markForCheck();
            return;
        }

        const keyInterventionTotal = 'cummulative_intervention_cost_current_fifteen';
        const keyHealthSectorTotal = 'cummulative_health_sector_cost_current_fifteen';
        const keyNetCost = 'cummulative_invention_health_sector_cost_current_fifteen';
        const keyQaly = 'cummulative_QALY_current_fifteen';
        const keyNetBenefitHSocial = 'net_benefit_health_and_social_sector_current_fifteen';

        const c4cIntervention = this.valueAtStrict(currentCm, keyInterventionTotal, this.targetYear);
        const netCost = this.valueAtStrict(currentCm, keyNetCost, this.targetYear);
        const qaly = this.valueAtStrict(currentCm, keyQaly, this.targetYear);

        let savingsAbs: number | null = null;
        let netBenefitDelta: number | null = null;

        const hasUserSim = others.length > 0;
        if (hasUserSim && baseCm) {
            const baseHealth = this.valueAtStrict(baseCm, keyHealthSectorTotal, this.targetYear);
            const currHealth = this.valueAtStrict(currentCm, keyHealthSectorTotal, this.targetYear);
            savingsAbs = baseHealth != null && currHealth != null ? Math.abs(baseHealth - currHealth) : null;

            const baseNb = this.valueAtStrict(baseCm, keyNetBenefitHSocial, this.targetYear);
            const currNb = this.valueAtStrict(currentCm, keyNetBenefitHSocial, this.targetYear);
            netBenefitDelta = baseNb != null && currNb != null ? currNb - baseNb : null;
        }

        this.c4cInterventionCostDisplay = this.toCurrencyCompact(c4cIntervention);
        this.netCostDisplay = this.toCurrencyCompact(netCost);
        this.qalyDisplay = this.toNumberCompact(qaly);

        this.healthcareSavingsDisplay = hasUserSim ? this.toCurrencyCompact(savingsAbs) : '—';
        this.netBenefitDisplay = hasUserSim ? this.toCurrencyCompact(netBenefitDelta) : '—';

        this.cdr.markForCheck();
    }

    private resetDisplays(clearAll: boolean): void {
        if (clearAll) {
            this.c4cInterventionCostDisplay = '—';
            this.netCostDisplay = '—';
            this.qalyDisplay = '—';
        }
        this.healthcareSavingsDisplay = '—';
        this.netBenefitDisplay = '—';
    }

    private pickCostCm(run: any): CmShape | null {
        if (run?.costCm && typeof run.costCm === 'object') return run.costCm as CmShape;

        const candidates = [
            run?.payload?.[0]?.response?.costCm,
            run?.payload?.[0]?.response?.cm,
            run?.payload?.[0]?.request?.cm,
            run?.cm
        ].filter(Boolean);

        if (candidates.length > 0) return candidates[0] as CmShape;

        const deep = this.findBestCostCmDeep(run);
        return deep?.cm ?? null;
    }

    private valueAtStrict(cm: CmShape | null, key: string, year: YearKey): number | null {
        if (!cm) return null;
        const series = cm[key];
        if (!series) return null;
        if (!Object.prototype.hasOwnProperty.call(series, year)) return null;
        const v = series[year];
        const num = typeof v === 'number' ? v : Number(v ?? 0);
        return Number.isFinite(num) ? num : null;
    }

    private toCurrencyCompact(n: number | null): string {
        if (n == null || isNaN(n)) return '—';
        const abs = Math.abs(n);
        if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
        if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
    }

    private toNumberCompact(n: number | null): string {
        if (n == null || isNaN(n)) return '—';
        const abs = Math.abs(n);
        if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
        if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
    }

    private findBestCostCmDeep(root: any): { cm: CmShape; path: string } | null {
        const visited = new Set<any>();
        const stack: Array<{ node: any; path: string }> = [{ node: root, path: 'root' }];
        let best: { cm: CmShape; path: string; score: number } | null = null;
        const tokens = ['cost', 'investment', 'running', 'primary', 'secondary', 'health', 'sector', 'productivity', 'funeral', 'social', 'qaly', 'benefit'];

        while (stack.length) {
            const { node, path } = stack.pop()!;
            if (!node || typeof node !== 'object') continue;
            if (visited.has(node)) continue;
            visited.add(node);

            const score = this.scoreCostCandidate(node, tokens);
            if (score >= 2) {
                if (!best || score > best.score) best = { cm: node as CmShape, path, score };
            }

            if (Array.isArray(node)) {
                node.forEach((item, i) => stack.push({ node: item, path: `${path}[${i}]` }));
            } else {
                for (const key of Object.keys(node)) {
                    stack.push({ node: (node as any)[key], path: `${path}.${key}` });
                }
            }
        }

        return best ? { cm: best.cm, path: best.path } : null;
    }

    private scoreCostCandidate(obj: any, tokens: string[]): number {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 0;
        let score = 0;
        for (const [k, v] of Object.entries(obj)) {
            const key = String(k).toLowerCase();
            const hit = tokens.some(t => key.includes(t));
            if (hit && this.isYearSeries(v)) score++;
        }
        return score;
    }

    private isYearSeries(obj: any): boolean {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
        const keys = Object.keys(obj);
        if (keys.length < 2) return false;
        let count = 0;
        for (const y of keys) if (/^\d{4}$/.test(y) && typeof (obj as any)[y] === 'number') count++;
        return count >= 1;
    }
}